import { SUPPORTED_LOCALES } from '../constants/app.js';
import { z } from 'zod';
import { paginationMetaSchema } from './pagination.js';
import { compareDecimal } from '../utils/decimal.js';
import { currencyCodeSchema, idSchema, moneySchema } from './common.js';
import { advanceRefundAttributionConfirmationSchema, advanceReceiptAttributionConfirmationSchema, } from './advance-attribution.js';
import { ledgerAccountSchema, ledgerDateSchema, journalActionSchema, } from './financial-accounting.js';
const positive = moneySchema.refine((value) => compareDecimal(value, '0') > 0);
const nonnegative = moneySchema.refine((value) => compareDecimal(value, '0') >= 0);
export const ledgerExchangeRateSchema = z
    .string()
    .regex(/^\d{1,14}(\.\d{1,10})?$/u)
    .refine((value) => compareDecimal(value, '0') > 0);
export const ledgerPartySchema = z.enum(['CUSTOMER', 'VENDOR']);
export const ledgerAllocationInputSchema = z
    .object({
    documentId: idSchema.optional(),
    vendorInvoiceId: idSchema.optional(),
    amount: positive,
    moneyAmount: positive,
    cashDiscountAmount: nonnegative.default('0'),
    effectiveDate: ledgerDateSchema,
    advanceAttribution: advanceReceiptAttributionConfirmationSchema.optional(),
})
    .refine((value) => Boolean(value.documentId) !== Boolean(value.vendorInvoiceId), {
    path: ['documentId'],
    message: 'Select exactly one invoice',
});
export const ledgerPaymentCreateSchema = z
    .object({
    party: ledgerPartySchema,
    partnerId: idSchema,
    direction: z.enum(['CREDIT', 'DEBIT']),
    bookingDate: ledgerDateSchema,
    valueDate: ledgerDateSchema.nullish(),
    moneyAccountId: idSchema,
    amount: positive,
    currency: currencyCodeSchema,
    exchangeRate: ledgerExchangeRateSchema.default('1'),
    exchangeRateDate: ledgerDateSchema.optional(),
    exchangeRateSource: z.string().trim().min(3).max(240).optional(),
    reference: z.string().trim().min(3).max(200),
    evidenceAttachmentId: idSchema.nullish(),
    feeAmount: nonnegative.default('0'),
    idempotencyKey: z.string().trim().min(8).max(100),
    allocations: z.array(ledgerAllocationInputSchema).max(100).default([]),
})
    .superRefine((input, ctx) => {
    const seen = new Set();
    input.allocations.forEach((allocation, index) => {
        const id = allocation.documentId ?? allocation.vendorInvoiceId ?? '';
        if (seen.has(id) || (input.party === 'CUSTOMER') !== Boolean(allocation.documentId))
            ctx.addIssue({
                code: 'custom',
                path: ['allocations', index, 'documentId'],
                message: 'Invoice is duplicated or belongs to the wrong subledger',
            });
        seen.add(id);
        if (allocation.effectiveDate < input.bookingDate)
            ctx.addIssue({
                code: 'custom',
                path: ['allocations', index, 'effectiveDate'],
                message: 'Allocation cannot precede the cash date',
            });
    });
});
export const ledgerAllocatePaymentSchema = z.object({
    version: z.number().int().positive(),
    party: ledgerPartySchema,
    idempotencyKey: z.string().trim().min(8).max(100),
    allocations: z.array(ledgerAllocationInputSchema).min(1).max(100),
});
export const ledgerPaymentReverseSchema = journalActionSchema.extend({
    party: ledgerPartySchema,
    bookingDate: ledgerDateSchema,
    reason: z.string().trim().min(10).max(2000),
});
export const ledgerPaymentQuerySchema = z
    .object({
    party: ledgerPartySchema.optional(),
    partnerId: idSchema.optional(),
    unallocatedOnly: z
        .preprocess((value) => (value === 'true' ? true : value === 'false' ? false : value), z.boolean())
        .optional(),
    moneyAccountId: idSchema.optional(),
    direction: z.enum(['CREDIT', 'DEBIT']).optional(),
    currency: currencyCodeSchema.optional(),
    status: z.enum(['ACTIVE', 'REVERSED']).optional(),
    dateFrom: ledgerDateSchema.optional(),
    dateTo: ledgerDateSchema.optional(),
    search: z.string().max(200).optional(),
    page: z.coerce.number().int().positive().max(10000).default(1),
    pageSize: z.coerce.number().int().positive().max(200).default(50),
    sort: z
        .enum(['bookingDate:asc', 'bookingDate:desc', 'amount:asc', 'amount:desc'])
        .default('bookingDate:desc'),
})
    .refine((query) => !query.sort.startsWith('amount:') || Boolean(query.currency), {
    path: ['currency'],
    message: 'Select a currency before sorting amounts',
});
export const ledgerPaymentAllocationSchema = z.object({
    id: idSchema,
    documentId: idSchema.nullable(),
    vendorInvoiceId: idSchema.nullable(),
    invoiceNumber: z.string(),
    invoiceCurrency: currencyCodeSchema,
    amount: moneySchema,
    moneyAmount: moneySchema.nullable(),
    cashDiscountAmount: moneySchema,
    effectiveDate: ledgerDateSchema.nullable(),
});
export const ledgerPaymentEventSchema = z.object({
    id: idSchema,
    kind: z.string(),
    sourceInvoiceId: idSchema.nullable().optional(),
    refundPaymentId: idSchema.nullable().optional(),
    targetType: z.enum(['INVOICE', 'PAYMENT']).default('INVOICE'),
    counterpartPaymentId: idSchema.nullable().default(null),
    effectiveDate: ledgerDateSchema,
    invoiceNumber: z.string(),
    invoiceCurrency: currencyCodeSchema,
    invoiceAmount: moneySchema,
    moneyAmount: moneySchema,
    discountAmount: moneySchema,
    journalId: idSchema.nullable(),
    reversalOfId: idSchema.nullable(),
    reversed: z.boolean(),
    reason: z.string().nullable(),
    createdAt: z.string().datetime(),
    createdById: idSchema,
});
export const ledgerPaymentSchema = z.object({
    id: idSchema,
    party: ledgerPartySchema,
    partnerId: idSchema,
    partnerName: z.string(),
    direction: z.enum(['CREDIT', 'DEBIT']),
    bookingDate: ledgerDateSchema,
    valueDate: ledgerDateSchema.nullable(),
    amount: moneySchema,
    currency: currencyCodeSchema,
    moneyAccountId: idSchema.nullable(),
    moneyAccountNumber: z.string().nullable(),
    moneyAccountName: z.string().nullable(),
    unallocatedAmount: moneySchema,
    homeAmount: moneySchema.nullable(),
    exchangeRate: ledgerExchangeRateSchema.nullable(),
    exchangeRateDate: ledgerDateSchema.nullable(),
    exchangeRateSource: z.string().nullable(),
    reference: z.string().nullable(),
    evidenceReference: z.string().nullable(),
    evidenceAttachmentId: idSchema.nullable(),
    feeAmount: moneySchema,
    ledgerJournalId: idSchema.nullable(),
    bankTransactionId: idSchema.nullable(),
    source: z.enum(['MANUAL', 'BANK_IMPORT']),
    status: z.enum(['ACTIVE', 'REVERSED']),
    reversedOn: ledgerDateSchema.nullable(),
    reversalReason: z.string().nullable(),
    version: z.number().int().positive(),
    allocations: z.array(ledgerPaymentAllocationSchema),
    history: z.array(ledgerPaymentEventSchema).default([]),
});
export const ledgerPaymentListSchema = z.object({
    data: z.array(ledgerPaymentSchema),
    meta: paginationMetaSchema,
});
export const ledgerPaymentWorkspaceSchema = z.object({
    businessDate: ledgerDateSchema,
    enabled: z.boolean(),
    currency: currencyCodeSchema,
    accounts: z.array(ledgerAccountSchema),
});
export const ledgerOpenItemsQuerySchema = z.object({
    status: z.enum(['OPEN', 'OVERDUE', 'CREDIT', 'SETTLED']).optional(),
    asOfDate: ledgerDateSchema.optional(),
    party: ledgerPartySchema.optional(),
    partnerId: idSchema.optional(),
    currency: currencyCodeSchema.optional(),
    includeSettled: z.preprocess((value) => (value === 'false' ? false : value === 'true' ? true : value), z.boolean().default(false)),
    search: z.string().max(200).optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(200).default(50),
});
export const ledgerOpenItemSchema = z.object({
    id: idSchema,
    party: ledgerPartySchema,
    documentId: idSchema.nullable(),
    vendorInvoiceId: idSchema.nullable(),
    partnerId: idSchema,
    partnerName: z.string(),
    number: z.string(),
    recognitionDate: ledgerDateSchema,
    dueDate: ledgerDateSchema.nullable(),
    currency: currencyCodeSchema,
    grossAmount: moneySchema,
    settledAmount: moneySchema,
    discountAmount: moneySchema,
    openAmount: moneySchema,
    openHomeAmount: moneySchema,
    status: z.enum(['OPEN', 'OVERDUE', 'CREDIT', 'SETTLED']),
    journalId: idSchema,
    mode: z.enum(['INVOICE_POSTING', 'ADOPTED_OPENING', 'PAYMENT_CREDIT']),
});
export const ledgerOpenItemsSchema = z.object({
    asOfDate: ledgerDateSchema,
    homeCurrency: currencyCodeSchema,
    data: z.array(ledgerOpenItemSchema),
    meta: paginationMetaSchema,
    totals: z.array(z.object({
        currency: currencyCodeSchema,
        receivables: moneySchema,
        payables: moneySchema,
        customerCredits: moneySchema,
        vendorCredits: moneySchema,
        overdueReceivables: moneySchema,
        overduePayables: moneySchema,
    })),
});
export const ledgerTransferCreateSchema = z
    .object({
    fromAccountId: idSchema,
    toAccountId: idSchema,
    outgoingDate: ledgerDateSchema,
    incomingDate: ledgerDateSchema.nullish(),
    outgoingAmount: positive,
    feeAmount: nonnegative.default('0'),
    incomingFeeAmount: nonnegative.default('0'),
    incomingAmount: positive.nullish(),
    outgoingCurrency: currencyCodeSchema,
    incomingCurrency: currencyCodeSchema,
    outgoingRate: ledgerExchangeRateSchema.default('1'),
    incomingRate: ledgerExchangeRateSchema.nullish(),
    exchangeRateSource: z.string().trim().min(3).max(240),
    reference: z.string().trim().min(3).max(200),
    idempotencyKey: z.string().min(8).max(100),
})
    .superRefine((value, ctx) => {
    if (compareDecimal(value.feeAmount, value.outgoingAmount) >= 0)
        ctx.addIssue({
            code: 'custom',
            path: ['feeAmount'],
            message: 'ledger.errors.paymentExceeded',
        });
    if (value.fromAccountId === value.toAccountId)
        ctx.addIssue({
            code: 'custom',
            path: ['toAccountId'],
            message: 'Choose different own accounts',
        });
    if (!value.incomingDate && compareDecimal(value.incomingFeeAmount, '0') > 0)
        ctx.addIssue({ code: 'custom', path: ['incomingDate'], message: 'ledger.errors.validation' });
    const incoming = [value.incomingDate, value.incomingAmount, value.incomingRate];
    if (incoming.some((v) => v !== null && v !== undefined) &&
        !incoming.every((v) => v !== null && v !== undefined))
        ctx.addIssue({
            code: 'custom',
            path: ['incomingDate'],
            message: 'Incoming cash date, amount and exchange rate are required together',
        });
    if (value.incomingDate && value.incomingDate < value.outgoingDate)
        ctx.addIssue({
            code: 'custom',
            path: ['incomingDate'],
            message: 'Incoming cash precedes outgoing cash',
        });
});
export const ledgerTransferSchema = z.object({
    id: idSchema,
    fromAccountNumber: z.string(),
    fromAccountName: z.string(),
    toAccountNumber: z.string(),
    toAccountName: z.string(),
    feeAmount: moneySchema.nullable(),
    incomingFeeAmount: moneySchema.default('0'),
    status: z.enum(['PENDING', 'RECEIVED', 'REVERSED']),
    outgoingJournalId: idSchema.nullable(),
    incomingJournalId: idSchema.nullable(),
    reversedOn: ledgerDateSchema.nullable(),
    incomingRateSource: z.string().nullable(),
    fromAccountId: idSchema,
    toAccountId: idSchema,
    outgoingDate: ledgerDateSchema.nullable(),
    incomingDate: ledgerDateSchema.nullable(),
    outgoingAmount: moneySchema.nullable(),
    incomingAmount: moneySchema.nullable(),
    outgoingCurrency: currencyCodeSchema,
    incomingCurrency: currencyCodeSchema,
    outgoingRate: ledgerExchangeRateSchema.nullable(),
    incomingRate: ledgerExchangeRateSchema.nullable(),
    exchangeRateSource: z.string(),
    reference: z.string(),
    pendingLeg: z.enum(['OUT', 'IN']).nullable().default(null),
    outgoingBankTransactionId: idSchema.nullable().default(null),
    incomingBankTransactionId: idSchema.nullable().default(null),
    outgoingRateSource: z.string().nullable().default(null),
    version: z.number(),
});
export const ledgerTransferListSchema = z.object({
    data: z.array(ledgerTransferSchema),
    meta: paginationMetaSchema,
});
export const ledgerTransferQuerySchema = z.object({
    page: z.coerce.number().int().positive().max(10000).default(1),
    pageSize: z.coerce.number().int().positive().max(200).default(50),
    search: z.string().trim().max(200).optional(),
    status: z.enum(['PENDING', 'RECEIVED', 'REVERSED']).optional(),
    dateFrom: ledgerDateSchema.optional(),
    dateTo: ledgerDateSchema.optional(),
    sort: z.enum(['outgoingDate:asc', 'outgoingDate:desc']).default('outgoingDate:desc'),
});
export const ledgerTransferReverseSchema = journalActionSchema.extend({
    bookingDate: ledgerDateSchema,
    reason: z.string().trim().min(10).max(2000),
});
export const ledgerTransferCompleteSchema = journalActionSchema.extend({
    incomingDate: ledgerDateSchema,
    incomingAmount: positive,
    incomingFeeAmount: nonnegative.default('0'),
    incomingRate: ledgerExchangeRateSchema,
    exchangeRateSource: z.string().trim().min(3).max(240),
});
export const ledgerPaymentPartnerQuerySchema = z.object({
    party: ledgerPartySchema,
    search: z.string().trim().max(200).default(''),
});
export const ledgerPaymentPartnersSchema = z.object({
    data: z.array(z.object({ id: idSchema, number: z.string(), label: z.string(), currency: currencyCodeSchema })),
});
export const ledgerTransferExportQuerySchema = ledgerTransferQuerySchema.extend({
    locale: z.enum(SUPPORTED_LOCALES).default('de'),
});
export const ledgerAdvanceRefundSchema = z.object({
    party: ledgerPartySchema,
    paymentId: idSchema,
    paymentVersion: z.number().int().positive(),
    allocationId: idSchema,
    amount: positive.optional(),
    advanceAttribution: advanceRefundAttributionConfirmationSchema.optional(),
    refundPaymentId: idSchema,
    refundPaymentVersion: z.number().int().positive(),
    bookingDate: ledgerDateSchema,
    reason: z.string().trim().min(10).max(2000),
    idempotencyKey: z.string().trim().min(8).max(100),
});
export const ledgerPaymentOffsetCreateSchema = z.object({
    party: ledgerPartySchema,
    version: z.number().int().positive(),
    counterpartVersion: z.number().int().positive(),
    counterpartPaymentId: idSchema,
    amount: positive,
    counterpartAmount: positive,
    bookingDate: ledgerDateSchema,
    reason: z.string().trim().min(10).max(2000),
    exchangeRateSource: z.string().trim().min(3).max(240).optional(),
    idempotencyKey: z.string().trim().min(8).max(100),
});
//# sourceMappingURL=ledger-payments.js.map